/**
 * 视频列表页面 - 类似哔哩哔哩的视频展示
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './VideoList.css';
import './common/ListStatus.css';
import { API_BASE_URL } from '../config/api';
import { getAuthHeaders, clearAuth } from '../utils/auth';
import type { VideoDescriptor } from '../types/api';
import { getVideoCoverUrl, handleCoverError } from '../utils/videoCover';
import { handleApiResponse, getErrorMessage } from '../utils/errorHandler';
import { videoListCache } from '../utils/cache';
import { useInfiniteObserver } from '../hooks/useInfiniteObserver';
import ListStatusFooter from './common/ListStatusFooter';
import { Select, SelectItem } from './Select';
import { Input } from './Input';

// 下拉刷新hook
const usePullToRefresh = (onRefresh: () => Promise<void>, threshold: number = 80) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [startY, setStartY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    const scrollTop = containerRef.current?.scrollTop || 0;
    // 只有在顶部时才允许下拉刷新
    if (scrollTop <= 0) {
      setStartY(e.touches[0].clientY);
      setIsDragging(true);
    }
  }, []);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isDragging) return;

    const currentY = e.touches[0].clientY;
    const deltaY = currentY - startY;

    if (deltaY > 0) { // 向下拉
      const distance = Math.min(deltaY * 0.5, threshold * 2); // 阻尼效果
      setPullDistance(distance);
    }
  }, [isDragging, startY, threshold]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;

    setIsDragging(false);

    if (pullDistance >= threshold) {
      // 触发刷新
      setPullDistance(threshold); // 保持在阈值位置

      onRefresh().finally(() => {
        setPullDistance(0);
      });
    } else {
      // 回弹
      setPullDistance(0);
    }
  }, [isDragging, pullDistance, threshold, onRefresh]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: true });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  return {
    pullDistance,
    isDragging,
    containerRef
  };
};

// 语言代码到中文名称的映射
const getLanguageName = (langCode: string): string => {
  const languageNames: Record<string, string> = {
    'zh': '中文',
    'en': '英语',
    'ja': '日语',
    'ko': '韩语',
    'es': '西班牙语',
    'fr': '法语',
    'de': '德语',
    'ru': '俄语',
  };
  return languageNames[langCode] || langCode;
};

const VideoList: React.FC = () => {
  // 【边界 1】：VideoDescriptor 是 UI 层唯一允许使用的读模型
  const [videos, setVideos] = useState<VideoDescriptor[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [refreshing, setRefreshing] = useState<boolean>(false);

  // 下拉刷新
  const handleRefreshRef = useRef<() => Promise<void> | null>(null);
  const { pullDistance, isDragging, containerRef } = usePullToRefresh(async () => {
    if (handleRefreshRef.current) {
      await handleRefreshRef.current();
    }
  }, 80);
  // 筛选 / 搜索状态
  const [searchKeyword, setSearchKeyword] = useState<string>(''); // 输入框的值（不触发搜索）
  const [searchKeywordActive, setSearchKeywordActive] = useState<string>(''); // 实际用于搜索的关键词（点击放大镜时设置）
  const [sourceLanguageFilter, setSourceLanguageFilter] = useState<string>(''); // 空代表不过滤
  const navigate = useNavigate();

  // 用于防重复请求和防抖
  const loadingRef = useRef<boolean>(false);

  const PAGE_SIZE = 20;


  // 首次加载 & 当筛选条件变化时重新加载第一页
  useEffect(() => {
    // 当筛选条件变化时，清空相关缓存
    if (searchKeywordActive !== '' || sourceLanguageFilter !== '') {
      console.log('[VideoList] 筛选条件变化，清空缓存');
      videoListCache.clear();
    }

    // 重置为第一页重新加载
    setCurrentPage(1);
    setHasMore(true);
    loadVideos(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKeywordActive, sourceLanguageFilter]);

  // 当搜索框被清空时，自动触发搜索（清空搜索条件）
  useEffect(() => {
    if (searchKeyword.trim() === '' && searchKeywordActive.trim() !== '') {
      // 如果搜索框被清空，但当前还有激活的搜索关键词，则自动清空搜索
      setSearchKeywordActive('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKeyword]);

  interface VideoListResponse {
    list: VideoDescriptor[];
    page: number;
    pageSize: number;
    hasMore: boolean;
  }

  const loadVideos = useCallback(async (page: number, append: boolean = false) => {
    // 防重复请求
    if (loadingRef.current) {
      return;
    }

    loadingRef.current = true;

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      // 构建查询参数（分页 + 筛选条件）
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', String(PAGE_SIZE));
      if (searchKeywordActive.trim()) {
        params.set('search', searchKeywordActive.trim());
      }
      if (sourceLanguageFilter.trim()) {
        params.set('sourceLanguage', sourceLanguageFilter.trim());
      }

      const searchParam = searchKeywordActive.trim();
      const languageParam = sourceLanguageFilter.trim();

      // 尝试从缓存获取数据（仅第一页且非追加模式）
      if (page === 1 && !append) {
        const cachedData = videoListCache.get(page, searchParam, languageParam);
        if (cachedData) {
          console.log('[VideoList] 使用缓存数据');
          setVideos(cachedData.list);
          setHasMore(cachedData.hasMore);
          setCurrentPage(page);
          setError(null);
          return;
        }
      }

      const response = await fetch(
        `${API_BASE_URL}/video-list?${params.toString()}`,
        {
          headers: getAuthHeaders(),
        }
      );

        // 如果是 401 未授权，清除认证信息并重定向到登录页
        if (response.status === 401) {
          clearAuth();
          navigate('/login');
          return;
        }

      // 使用统一的API响应处理
      const data = await handleApiResponse<VideoListResponse>(response);

      // 缓存第一页数据
      if (page === 1) {
        videoListCache.set(page, searchParam, languageParam, data);
      }
      
      // 【边界 1】：后端已过滤，只返回符合条件的视频（public + completed + 有完整双语字幕）
      // 前端不再需要过滤，直接使用返回的列表
      const videoList = data.list || [];

      if (append) {
        setVideos(prev => [...prev, ...videoList]);
      } else {
        setVideos(videoList);
      }

      setHasMore(data.hasMore);
      setCurrentPage(page);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingRef.current = false;
    }
  }, [navigate, searchKeywordActive, sourceLanguageFilter]);

  // 刷新处理函数 - 清空缓存并重新加载第一页
  const handleRefresh = useCallback(async () => {
    console.log('[VideoList] 下拉刷新，清空缓存并重新加载');
    setRefreshing(true);

    try {
      // 清空缓存
      videoListCache.clear();

      // 重置状态
      setCurrentPage(1);
      setHasMore(true);
      setError(null);

      // 重新加载第一页
      await loadVideos(1, false);
    } catch (err) {
      console.error('[VideoList] 刷新失败:', err);
      setError('刷新失败，请稍后重试');
    } finally {
      setRefreshing(false);
    }
  }, [loadVideos]);

  // 设置刷新函数引用
  useEffect(() => {
    handleRefreshRef.current = handleRefresh;
  }, [handleRefresh]);

  // Infinite scroll hook
  const onLoadMore = useCallback(() => {
    loadVideos(currentPage + 1, true);
  }, [currentPage, loadVideos]);

  const { sentinelRef } = useInfiniteObserver({
    enabled: true,
    hasMore,
    isLoading: loadingRef.current || loadingMore,
    onLoadMore,
    rootMargin: '100px',
    threshold: 0.1,
  });

  // 处理搜索按钮点击
  const handleSearch = () => {
    setSearchKeywordActive(searchKeyword);
  };

  // 处理回车键搜索
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };


  const handleVideoClick = (video: VideoDescriptor) => {
    // 从首页进入，返回时回到首页，并把当前卡片的标题一起带给播放器作为初始标题
    navigate(`/player?video=${encodeURIComponent(video.videoId)}&from=list`, {
      state: {
        initialTitle: video.name || video.videoId,
      },
    });
  };


  if (loading && !loadingMore && videos.length === 0) {
    return (
      <div className="loading">加载中...</div>
    );
  }

  if (error) {
    return (
      <div className="error">错误: {error}</div>
    );
  }

  return (
    <>
      {/* 吸顶交互控件区域 - 保持固定不动 */}
      <div className="video-filters-sticky">
        <div className="video-filters-sticky-row">
          <div className="filter-group filter-search">
            <div className="search-input-wrapper">
              <Input
                type="text"
                placeholder="输入关键字，按名称模糊搜索"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className="search-input"
              />
              <div className="search-actions">
                <button
                  type="button"
                  className="search-button"
                  onClick={handleSearch}
                  aria-label="搜索"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                  </svg>
                </button>
                <button
                  type="button"
                  className="refresh-button"
                  onClick={handleRefresh}
                  aria-label="刷新"
                  title="刷新首页数据 (Ctrl+R)"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="23,4 23,10 17,10"></polyline>
                    <polyline points="1,20 1,14 7,14"></polyline>
                    <path d="M20.49,9A9,9,0,0,0,5.64,5.64L1,10m22,4l-4.64,4.36A9,9,0,0,1,3.51,15"></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <div className="filter-group filter-language">
            <Select
              value={sourceLanguageFilter || "all"}
              onValueChange={(value) => setSourceLanguageFilter(value === "all" ? "" : value)}
              aria-label="筛选语言（仅限 英语 / 日语 / 韩语）"
            >
              {/* Only allow English, Japanese, Korean per requirements */}
              <SelectItem value="all">全部语言</SelectItem>
              <SelectItem value="en">英语</SelectItem>
              <SelectItem value="ja">日语</SelectItem>
              <SelectItem value="ko">韩语</SelectItem>
            </Select>
          </div>
        </div>
      </div>

      {/* 下拉刷新容器 - 只包含视频网格部分 */}
      <div
        ref={containerRef}
        className="video-list-container"
        style={{
          transform: pullDistance > 0 ? `translateY(${pullDistance}px)` : undefined,
          transition: isDragging ? 'none' : 'transform 0.3s ease-out'
        }}
      >
        {/* 下拉刷新指示器 */}
        <div
          className="pull-to-refresh-indicator"
          style={{
            height: Math.max(pullDistance, 60),
            opacity: pullDistance > 20 ? 1 : 0,
            transition: isDragging ? 'none' : 'all 0.3s ease-out'
          }}
        >
          <div className="pull-to-refresh-content">
            {refreshing ? (
              <>
                <svg
                  className="refresh-spinner spinning"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                  <path d="M21 3v5h-5"/>
                  <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                  <path d="M8 16H3v5"/>
                </svg>
                <span>正在刷新...</span>
              </>
            ) : pullDistance >= 80 ? (
              <>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="20,6 9,17 4,12"></polyline>
                </svg>
                <span>释放刷新</span>
              </>
            ) : (
              <>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{
                    transform: `rotate(${pullDistance * 3}deg)`,
                    transition: isDragging ? 'none' : 'transform 0.3s ease-out'
                  }}
                >
                  <polyline points="6,9 12,15 18,9"></polyline>
                </svg>
                <span>下拉刷新</span>
              </>
            )}
          </div>
        </div>

        <div className="video-grid">
        {videos.map((video) => (
          <div
            key={video.videoId}
            className="vcard"
            onClick={() => handleVideoClick(video)}
          >
            {/* 视频区 - 承载图片和所有角标 */}
            <div className="vcard__thumb">
              <img
                src={getVideoCoverUrl(video)}
                alt={video.name || video.videoId}
                onError={handleCoverError}
                loading="lazy"
                decoding="async"
              />
              <div className="thumbnail-placeholder" style={{ display: 'none' }}>
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <polygon points="5 3 19 12 5 21 5 3"></polygon>
                </svg>
              </div>
              {/* 右上角标 */}
              {video.capabilities.hasWordAlignment && (
                <div className="video-badge">
                  <span className="icon-inline" aria-hidden="true"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l2 5 5 .5-4 3 1 5-4-2-4 2 1-5-4-3 5-.5z"></path></svg></span>
                  <span>词级对齐</span>
                </div>
              )}
              {/* 右下时长 */}
              {(video as any)._meta?.durationFormatted && (video as any)._meta.durationFormatted !== '未知' && (
                <div className="video-duration">
                  <span>{(video as any)._meta.durationFormatted}</span>
                </div>
              )}
              {/* 左下观看次数 */}
              {(video as any)._meta?.viewCount !== undefined && (
                <div className="video-view-count">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                  <span>{(video as any)._meta.viewCount}</span>
                </div>
              )}
            </div>

            {/* 标题区 - 固定高度容器 */}
            <div
              className="vcard__title"
              data-text={video.name || video.videoId}
            />

            {/* 标签区 - 语言和观看数标签 */}
            <div className="vcard__tags">
              {video.sourceLanguage && (
                <span className="vcard__tag vcard__tag--lang">
                  {getLanguageName(video.sourceLanguage)}
                </span>
              )}
              {/* 其他标签可以在这里添加 */}
            </div>
          </div>
        ))}
      </div>

      {videos.length === 0 && !loading && (
        <div className="empty-state">
          <p>暂无视频</p>
        </div>
      )}

        {/* 滚动加载触发器 */}
        <ListStatusFooter
          ref={sentinelRef}
          loadingMore={loadingMore}
          hasMore={hasMore}
          allowNoMore={videos.length > 0}
        />
      </div>
    </>
  );
};

export default VideoList;

